'use client';

/**
 * Suggestion / Suggestions — adapted from AI Elements (https://elements.ai-sdk.dev/components/suggestion)
 * Suggestion is a super simple Aura tertiary button with an optional icon (defaults to Search).
 * Suggestions is a horizontal scroll container with fade indicators at the edges when content overflows;
 * pass a ref to wire up custom scroll controls (e.g. chevron buttons).
 *
 * TODO:
 * - figure out what should we do when it breakes into multiple lines
 *
 */

import {
  loadDomMax,
  type SvgIcon,
  useIsHorizontalOverflow,
  useScrollOverflowMask,
  useScrollProgress,
} from '@bridebook/aura';
import { LazyMotion } from 'motion/react';
import * as m from 'motion/react-m';
import {
  type ComponentProps,
  forwardRef,
  type HTMLAttributes,
  useImperativeHandle,
  useRef,
} from 'react';

import { Button } from '../../button/button';
import { cn } from '../../../lib/utils/cn';
import { Search } from '../../../icons';

export type SuggestionsProps = HTMLAttributes<HTMLDivElement> & {
  classNames?: {
    scrollContainer?: string; // wrapper for the scroll container
    innerWrapper?: string;
  };
};

export const Suggestions = forwardRef<HTMLDivElement, SuggestionsProps>(
  ({ className, children, classNames, ...props }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { scrollXProgress } = useScrollProgress({ container: scrollRef });
    const maskImage = useScrollOverflowMask(scrollXProgress);
    const isOverflow = useIsHorizontalOverflow(scrollRef);

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => scrollRef.current);

    return (
      <LazyMotion features={loadDomMax} strict>
        <m.div
          style={{
            maskImage: isOverflow ? maskImage : undefined,
          }}
          className={cn('relative w-full', className)}>
          <div
            ref={scrollRef}
            className={cn(
              'scrollbar-hidden w-full touch-pan-x overflow-x-auto overflow-y-hidden whitespace-nowrap',
              classNames?.scrollContainer,
            )}
            {...props}>
            <div
              className={cn(
                'flex w-max min-w-0 flex-nowrap items-center gap-2',
                classNames?.innerWrapper,
              )}>
              {children}
            </div>
          </div>
        </m.div>
      </LazyMotion>
    );
  },
);

Suggestions.displayName = 'Suggestions';

export type SuggestionProps = Omit<ComponentProps<typeof Button>, 'icon'> & {
  /** Left icon. Defaults to Search. Pass `null` for no icon. */
  icon?: SvgIcon | null;
};

export const Suggestion = ({
  className,
  size = 'sm',
  children,
  icon = Search,
  ...props
}: SuggestionProps) => (
  <Button
    className={cn('h-auto w-fit justify-start py-2 text-left text-wrap', className)}
    size={size}
    type="button"
    variant="tertiary"
    icon={icon ?? undefined}
    {...props}>
    {children}
  </Button>
);
